import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Select, Button, Table, Input, message, Modal, Statistic, Progress, DatePicker, Popconfirm, Space } from 'antd';
import { DollarOutlined, FileAddOutlined, FileTextOutlined, SendOutlined, BarChartOutlined, DownloadOutlined, SearchOutlined, ReloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { buildAPIURL } from '../../config/apiConfig';
import { list as listTuition, create as createTuition, issueInvoice as issueInvoiceAPI, getConfig as getTuitionConfig } from '../../services/tuitionService';
import { listConfigs, upsertConfig, deleteConfig } from '../../services/tuitionService';
import * as XLSX from 'xlsx';

interface Course { KhoaHocID: string; TenKhoaHoc: string; }
interface ClassSection { LopHocID: string; TenLop: string; }
interface Student { MaHS: string; HoTen?: string; Hoten?: string; NgaySinh?: string; TenLop?: string; }

const apiBase = buildAPIURL('');

const TuitionManagementPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [classes, setClasses] = useState<ClassSection[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [search, setSearch] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<string>();
  const [selectedClass, setSelectedClass] = useState<string>();

  // Tuition creation modal state
  const [tuitionModalOpen, setTuitionModalOpen] = useState(false);
  const [tuitionForm, setTuitionForm] = useState<{ MaHS?: string; ItemName?: string; Quantity?: number; UnitPrice?: number; Semester?: string; Year?: string; PaymentMethod?: string; buyerName?: string; buyerEmail?: string; buyerPhoneNumber?: string; buyerAddressLine?: string; buyerUnitName?: string; templateCode?: string; invoiceSeries?: string; sellerTaxCode?: string; invoiceTemplateId?: number; }>({ Quantity: 1, UnitPrice: 0, PaymentMethod: 'Tiền mặt' });
  const [availableConfigs, setAvailableConfigs] = useState<Array<{Semester:string; Year:string; ItemName:string; UnitPrice:number}>>([]);

  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configs, setConfigs] = useState<any[]>([]);
  const [configForm, setConfigForm] = useState<{ ConfigID?: number; KhoaHocID?: string; Semester?: string; Year?: string; ItemName?: string; UnitName?: string; UnitPrice?: number }>({ UnitName: 'Kỳ' });
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  const [statModalOpen, setStatModalOpen] = useState(false);
  const [statData, setStatData] = useState<{ total: number; paid: number; unpaid: number; totalAmount: number; tmCount?: number; ckCount?: number; details: any[] }>({ total: 0, paid: 0, unpaid: 0, totalAmount: 0, tmCount: 0, ckCount: 0, details: [] });
  const [classStatModalOpen, setClassStatModalOpen] = useState(false);
  const [classStatData, setClassStatData] = useState<{ total: number; paid: number; unpaid: number; totalAmount: number; paidStudents: any[]; unpaidStudents: any[] }>({ total: 0, paid: 0, unpaid: 0, totalAmount: 0, paidStudents: [], unpaidStudents: [] });
  const [lookupModalOpen, setLookupModalOpen] = useState(false);
  const [lookupForm, setLookupForm] = useState<{ supplierTaxCode?: string; startDate?: string; endDate?: string; buyerName?: string; invoiceType?: string; templateCode?: string; invoiceSeri?: string; result?: any[] }>({});
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<number>(0);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncForm, setSyncForm] = useState<{ startDate?: string; endDate?: string; supplierTaxCode?: string; invoiceSeri?: string; invoiceType?: string }>({ invoiceSeri: 'C22TYT', invoiceType: '02GTTT', supplierTaxCode: '1000359841' });
  const [syncResult, setSyncResult] = useState<{ total?: number; succeeded?: number; failed?: number } | null>(null);
  const [syncTimer, setSyncTimer] = useState<any>(null);

  const filteredStudents = useMemo(() => {
    if (!search) { return students; }
    const s = search.toLowerCase();
    return students.filter(x => ((x.HoTen || x.Hoten || '') as string).toLowerCase().includes(s) || (x.MaHS || '').toLowerCase().includes(s));
  }, [students, search]);

  useEffect(() => { loadCourses(); }, []);

  function authHeader() {
    return { 'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}` };
  }

  async function loadCourses() {
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/courses`, { headers: authHeader() });
      const data = await r.json();
      const arr = data?.data || (Array.isArray(data) ? data : []);
      setCourses(arr);
    } catch (e) {
      message.error('Không thể tải khóa học');
    } finally { setLoading(false); }
  }

  async function openConfigModal() {
    if (!selectedCourse) { message.warning('Chọn Khóa học trước'); return; }
    setConfigForm({ KhoaHocID: selectedCourse, UnitName: 'Kỳ' });
    setIsEditingConfig(false);
    const data = await listConfigs(selectedCourse);
    setConfigs(data);
    setConfigModalOpen(true);
  }

  async function saveConfig() {
    const { KhoaHocID, Semester, Year, ItemName, UnitName, UnitPrice } = configForm;
    if (!KhoaHocID || !Semester || !Year || !ItemName || UnitPrice === undefined) { message.error('Nhập đủ Khoa học, Kỳ, Năm, Tên khoản, Đơn giá'); return; }
    await upsertConfig({ KhoaHocID, Semester, Year, ItemName, UnitName, UnitPrice: Number(UnitPrice) });
    message.success(isEditingConfig ? 'Đã cập nhật cấu hình' : 'Đã lưu cấu hình');
    setIsEditingConfig(false);
    setConfigForm({ KhoaHocID: selectedCourse, UnitName: 'Kỳ' });
    const data = await listConfigs(selectedCourse);
    setConfigs(data);
  }

  function editConfig(record: any) {
    setConfigForm({
      ConfigID: record.ConfigID,
      KhoaHocID: record.KhoaHocID,
      Semester: record.Semester,
      Year: record.Year,
      ItemName: record.ItemName,
      UnitName: record.UnitName || 'Kỳ',
      UnitPrice: record.UnitPrice
    });
    setIsEditingConfig(true);
  }

  async function handleDeleteConfig(configId: number) {
    try {
      await deleteConfig(configId);
      message.success('Đã xóa cấu hình');
      const data = await listConfigs(selectedCourse);
      setConfigs(data);
    } catch (e: any) {
      message.error(e?.message || 'Xóa cấu hình thất bại');
    }
  }

  function resetConfigForm() {
    setConfigForm({ KhoaHocID: selectedCourse, UnitName: 'Kỳ' });
    setIsEditingConfig(false);
  }

  async function onCourseChange(khoaHocId: string) {
    setSelectedCourse(khoaHocId);
    setSelectedClass(undefined);
    setStudents([]);
    try {
      const r = await fetch(`${apiBase}/classes?khoaHocId=${encodeURIComponent(khoaHocId)}&limit=1000`, { headers: authHeader() });
      const data = await r.json();
      const arr = data?.data || [];
      setClasses(arr.map((c: any) => ({ LopHocID: c.LopHocID, TenLop: c.TenLop })));
    } catch {
      message.error('Không thể tải lớp');
    }
  }

  async function onGetStudents() {
    if (!selectedClass) { message.warning('Chọn lớp'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/sinhvien?lopHocId=${encodeURIComponent(selectedClass)}&limit=2000`, { headers: authHeader() });
      const data = await r.json();
      const arr = (data?.data || []).map((sv: any) => ({ ...sv, HoTen: sv.HoTen || sv.Hoten }));
      setStudents(arr);
      message.success(`Đã tải ${arr.length} sinh viên`);
    } catch (e) {
      message.error('Không thể tải sinh viên');
    } finally { setLoading(false); }
  }

  async function openCreateTuition(sv: Student) {
    const selectedCourseName = courses.find(c => c.KhoaHocID === (selectedCourse || ''))?.TenKhoaHoc || '';
    setTuitionForm({
      MaHS: sv.MaHS,
      ItemName: 'Học phí học kỳ',
      Quantity: 1,
      UnitPrice: 0,
      Semester: '',
      Year: '',
      PaymentMethod: 'Tiền mặt',
      buyerName: sv.HoTen || sv.Hoten,
      buyerEmail: '',
      buyerPhoneNumber: '',
      buyerAddressLine: `${sv.MaHS} | ${selectedCourseName} - ${sv.TenLop || ''}`,
      buyerUnitName: `${selectedCourseName} - ${sv.TenLop || ''}`,
      templateCode: '2/001',
      invoiceSeries: 'C22TYT',
      sellerTaxCode: '1000319841'
    });
    setTuitionModalOpen(true);
    try {
      if (selectedCourse) {
        const cfgs = await listConfigs(selectedCourse);
        setAvailableConfigs(cfgs.map((c:any)=>({ Semester:c.Semester, Year:c.Year, ItemName:c.ItemName, UnitPrice:c.UnitPrice })));
        if (Array.isArray(cfgs) && cfgs.length > 0) {
          const latest = cfgs[cfgs.length - 1];
          setTuitionForm(f => ({
            ...f,
            Semester: latest.Semester,
            Year: latest.Year,
            ItemName: latest.ItemName || f.ItemName,
            UnitPrice: Number(latest.UnitPrice) || f.UnitPrice
          }));
        }
      }
    } catch {}
  }

  async function submitCreateAndInvoice() {
    try {
      const { MaHS, ItemName, Quantity, UnitPrice, Semester, Year, PaymentMethod, buyerName, buyerEmail, buyerPhoneNumber, buyerAddressLine, templateCode, invoiceSeries, sellerTaxCode } = tuitionForm;
      // If missing ItemName/UnitPrice, try to load config as a fallback
      let computedItemName = ItemName;
      let computedUnitPrice = UnitPrice;
      if ((!computedItemName || !computedUnitPrice) && selectedCourse && tuitionForm.Semester && tuitionForm.Year) {
        const cfg = await getTuitionConfig(selectedCourse, tuitionForm.Semester, tuitionForm.Year);
        if (cfg) {
          computedItemName = computedItemName || cfg.ItemName;
          computedUnitPrice = computedUnitPrice || Number(cfg.UnitPrice) || 0;
          setTuitionForm(f => ({ ...f, ItemName: computedItemName, UnitPrice: computedUnitPrice }));
        }
      }

      if (!MaHS || !computedItemName || !Quantity || !computedUnitPrice || !templateCode || !invoiceSeries || !sellerTaxCode) {
        message.error('Thiếu dữ liệu bắt buộc'); return;
      }
      const tuitionId = await createTuition({ MaHS, ItemName: computedItemName, Quantity, UnitPrice: computedUnitPrice, Semester, Year, PaymentMethod });
      await issueInvoiceAPI({ tuitionId, templateCode, invoiceSeries, sellerTaxCode, buyerName, buyerEmail, buyerPhoneNumber, buyerAddressLine });
      message.success('Đã tạo học phí và gửi hóa đơn');
      setTuitionModalOpen(false);
    } catch (e: any) {
      message.error(`Lỗi: ${e?.message || 'Không xác định'}`);
    }
  }

  async function pushDraftToViettel() {
    try {
      const { MaHS, ItemName, Quantity, UnitPrice, Semester, Year, buyerName, buyerAddressLine, templateCode, invoiceSeries, PaymentMethod } = tuitionForm;
      if (!MaHS) { message.error('Thiếu mã SV'); return; }
      if (!templateCode || !invoiceSeries) { message.error('Thiếu Mẫu số/Ký hiệu'); return; }
      const tuitionId = await createTuition({ MaHS, ItemName: ItemName || 'Học phí', Quantity: Quantity || 1, UnitPrice: UnitPrice || 0, Semester, Year, PaymentMethod: PaymentMethod || 'Tiền mặt' });
      const resp = await fetch(`${apiBase}/tuition/portal/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}` },
        body: JSON.stringify({ tuitionId, templateCode, invoiceSeri: invoiceSeries, buyerName: buyerName || '', buyerAddress: buyerAddressLine || '', buyerUnitName: tuitionForm.buyerUnitName || buyerAddressLine || '', paymentMethod: PaymentMethod, invoiceTemplateId: tuitionForm.invoiceTemplateId })
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) { throw new Error(data?.message || 'Đẩy Viettel thất bại'); }
      message.success('Đã tạo nháp hóa đơn trên Viettel');
    } catch (e:any) {
      message.error(e?.message || 'Không đẩy được sang Viettel');
    }
  }

  function exportCSV() {
    const rows = filteredStudents.map(s => [s.MaHS, s.HoTen, s.TenLop || '', '']);
    const header = ['MaHS', 'HoTen', 'Lop', 'DaDongHocPhi'];
    const csv = [header, ...rows].map(r => r.map(x => '"' + String(x ?? '') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'thong_ke_hoc_phi.csv';
    link.click();
  }

  function exportExcel() {
    const rows = filteredStudents.map(s => ({ MaHS: s.MaHS, HoTen: s.HoTen || s.Hoten || '', Lop: s.TenLop || '', DaDongHocPhi: '' }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'ThongKeHocPhi');
    XLSX.writeFile(wb, 'thong_ke_hoc_phi.xlsx');
  }

  async function openStatistics() {
    if (!selectedCourse) { message.warning('Chọn Khóa học trước'); return; }
    try {
      const res = await fetch(`${apiBase}/tuition/statistics-by-course?khoaHocId=${encodeURIComponent(selectedCourse)}`, { headers: authHeader() });
      const data = await res.json();
      if (data?.success) {
        setStatData(data.data);
        setStatModalOpen(true);
      } else {
        message.error('Không thể tải thống kê');
      }
    } catch (e) { message.error('Không thể tải thống kê'); }
  }

  async function openClassStatistics() {
    if (!selectedClass) { message.warning('Chọn lớp trước'); return; }
    if (!selectedCourse) { message.warning('Chọn khóa học trước'); return; }
    setLoading(true);
    try {
      // Gọi API thống kê theo khóa học và lọc theo lớp
      const res = await fetch(`${apiBase}/tuition/statistics-by-course?khoaHocId=${encodeURIComponent(selectedCourse)}&lopHocId=${encodeURIComponent(selectedClass)}`, { headers: authHeader() });
      const data = await res.json();
      
      if (!data?.success) {
        throw new Error(data?.message || 'Không thể tải thống kê');
      }
      
      const stats = data.data;
      
      // Phân loại sinh viên đã/chưa nộp từ dữ liệu API
      const paidStudents: any[] = [];
      const unpaidStudents: any[] = [];
      
      (stats.details || []).forEach((student: any) => {
        if (student.PaymentStatus) {
          paidStudents.push(student);
        } else {
          unpaidStudents.push(student);
        }
      });
      
      setClassStatData({
        total: stats.total || 0,
        paid: stats.paid || 0,
        unpaid: stats.unpaid || 0,
        totalAmount: stats.totalAmount || 0,
        paidStudents,
        unpaidStudents
      });
      setClassStatModalOpen(true);
    } catch (e: any) {
      message.error(e?.message || 'Không thể tải thống kê lớp');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ padding: 16 }}>
      <Card title="Quản lý học phí sinh viên" bordered={false} extra={<Button icon={<BarChartOutlined />} onClick={openStatistics}>Thống kê</Button>}>
        <Row gutter={12} align="bottom">
          <Col span={8}>
            <label>Khóa học</label>
            <Select
              placeholder="Chọn khóa học"
              value={selectedCourse}
              options={courses.map(c => ({ value: c.KhoaHocID, label: c.TenKhoaHoc }))}
              onChange={onCourseChange}
              style={{ width: '100%' }}
              showSearch
              filterOption={(i,o)=> (o?.label as string).toLowerCase().includes(i.toLowerCase())}
            />
          </Col>
          <Col span={8}>
            <label>Lớp học</label>
            <Select
              placeholder="Chọn lớp học"
              value={selectedClass}
              options={classes.map(c => ({ value: c.LopHocID, label: `${c.LopHocID} - ${c.TenLop}` }))}
              onChange={setSelectedClass}
              style={{ width: '100%' }}
              disabled={!selectedCourse}
              showSearch
              filterOption={(i,o)=> (o?.label as string).toLowerCase().includes(i.toLowerCase())}
            />
          </Col>
          <Col span={4}>
            <label>Tìm kiếm</label>
            <Input placeholder="Tên/Mã SV" value={search} onChange={e=>setSearch(e.target.value)} />
          </Col>
          <Col span={4}>
            <Button type="primary" block onClick={onGetStudents} loading={loading} icon={<FileTextOutlined />}>Lấy danh sách</Button>
          </Col>
          <Col span={4}>
            <Button onClick={openConfigModal} block icon={<FileAddOutlined />}>Cấu hình</Button>
          </Col>
          <Col span={4}>
            <Button block icon={<SearchOutlined />} onClick={()=>setLookupModalOpen(true)}>Tra cứu HĐ</Button>
          </Col>
          <Col span={4}>
            <Button block icon={<ReloadOutlined />} disabled={!selectedClass} onClick={()=> setSyncModalOpen(true)}>Đồng bộ</Button>
          </Col>
        </Row>
      </Card>

      <Card style={{ marginTop: 12 }} bordered={false} title={`Sinh viên của lớp (${filteredStudents.length})`}>
        <Table
          rowKey="MaHS"
          dataSource={filteredStudents}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Mã SV', dataIndex: 'MaHS', width: 120 },
            { title: 'Họ và tên', key: 'HoTen', render: (_: any, rec: Student) => rec.HoTen || rec.Hoten || '' },
            { title: 'Lớp', dataIndex: 'TenLop', width: 200 },
            { title: 'Hành động', key: 'actions', width: 160, render: (_: any, sv: Student) => (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button icon={<SendOutlined />} type="primary" onClick={()=>openCreateTuition(sv)}>Nộp & xuất HĐ</Button>
              </div>
            )}
          ]}
        />
      </Card>

      <Modal
        open={tuitionModalOpen}
        onCancel={()=>setTuitionModalOpen(false)}
        title="Tạo học phí & xuất hóa đơn"
        footer={[
          <Button key="stats" icon={<BarChartOutlined />} onClick={()=>{setTuitionModalOpen(false); openClassStatistics();}}>Thống kê</Button>,
          <Button key="portal" icon={<SendOutlined />} onClick={pushDraftToViettel}>Đẩy Viettel</Button>,
          <Button key="cancel" onClick={()=>setTuitionModalOpen(false)}>Hủy</Button>
        ]}
      >
        <Row gutter={12}>
          <Col span={12}><label>Mã SV</label><Input value={tuitionForm.MaHS} onChange={e=>setTuitionForm({...tuitionForm, MaHS: e.target.value})} /></Col>
          <Col span={12}><label>Chọn kỳ</label>
            <Select
              value={tuitionForm.Semester && tuitionForm.Year ? `${tuitionForm.Semester} ${tuitionForm.Year}`: undefined}
              onChange={(val)=>{
                const [sem, ...rest] = val.split(' ');
                const year = rest.join(' ');
                const cfg = availableConfigs.find(c=>`${c.Semester} ${c.Year}`===val);
                setTuitionForm(f=>({ ...f, Semester: sem, Year: year, ItemName: cfg?.ItemName || f.ItemName, UnitPrice: cfg?.UnitPrice || f.UnitPrice }));
              }}
              style={{width:'100%'}}
              options={availableConfigs.map(c=>({ value: `${c.Semester} ${c.Year}`, label: `${c.Semester} - ${c.Year}` }))}
            />
          </Col>
        </Row>
        
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={12}><label>Tên khoản thu</label><Input value={tuitionForm.ItemName} onChange={e=>setTuitionForm({...tuitionForm, ItemName: e.target.value})} /></Col>
          <Col span={6}><label>Số lượng</label><Input type="number" value={tuitionForm.Quantity} onChange={e=>setTuitionForm({...tuitionForm, Quantity: Number(e.target.value)})} /></Col>
          <Col span={6}><label>Đơn giá</label><Input type="number" value={tuitionForm.UnitPrice} onChange={e=>setTuitionForm({...tuitionForm, UnitPrice: Number(e.target.value)})} /></Col>
        </Row>
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={12}><label>Phương thức</label><Select value={tuitionForm.PaymentMethod} onChange={v=>setTuitionForm({...tuitionForm, PaymentMethod: v})} style={{ width: '100%' }}
            options={[{value:'Tiền mặt',label:'Tiền mặt'},{value:'Chuyển khoản',label:'Chuyển khoản'}]} /></Col>
          <Col span={12}><Statistic title="Thành tiền" value={(Number(tuitionForm.Quantity)||0)*(Number(tuitionForm.UnitPrice)||0)} precision={0} suffix="đ" /></Col>
        </Row>
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={12}><label>Người mua hàng</label><Input value={tuitionForm.buyerName} onChange={e=>setTuitionForm({...tuitionForm, buyerName: e.target.value})} /></Col>
          <Col span={12}><label>Địa chỉ/ Lớp</label><Input value={tuitionForm.buyerAddressLine} onChange={e=>setTuitionForm({...tuitionForm, buyerAddressLine: e.target.value})} /></Col>
        </Row>
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={12}><label>buyerUnitName</label><Input value={tuitionForm.buyerUnitName} onChange={e=>setTuitionForm({...tuitionForm, buyerUnitName: e.target.value})} /></Col>
        </Row>
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={12}><label>Email</label><Input value={tuitionForm.buyerEmail} onChange={e=>setTuitionForm({...tuitionForm, buyerEmail: e.target.value})} /></Col>
          <Col span={12}><label>SĐT</label><Input value={tuitionForm.buyerPhoneNumber} onChange={e=>setTuitionForm({...tuitionForm, buyerPhoneNumber: e.target.value})} /></Col>
        </Row>
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={8}><label>Mẫu số</label><Input value={tuitionForm.templateCode} onChange={e=>setTuitionForm({...tuitionForm, templateCode: e.target.value})} /></Col>
          <Col span={8}><label>Ký hiệu</label><Input value={tuitionForm.invoiceSeries} onChange={e=>setTuitionForm({...tuitionForm, invoiceSeries: e.target.value})} /></Col>
          <Col span={8}><label>MST Bán</label><Input value={tuitionForm.sellerTaxCode} onChange={e=>setTuitionForm({...tuitionForm, sellerTaxCode: e.target.value})} /></Col>
        </Row>
      </Modal>

      <Modal open={lookupModalOpen} onCancel={()=>setLookupModalOpen(false)} title="Tra cứu số hóa đơn đã phát hành" footer={null} width={1100} styles={{ body: { maxHeight: 560, overflow: 'auto' } }}>
        <Row gutter={12}>
          <Col span={12}><label>MST bán</label><Input value={lookupForm.supplierTaxCode} onChange={e=>setLookupForm({...lookupForm, supplierTaxCode: e.target.value})} placeholder="VD: 1000319841" /></Col>
          <Col span={12}><label>Buyer name</label><Input value={lookupForm.buyerName} onChange={e=>setLookupForm({...lookupForm, buyerName: e.target.value})} placeholder="Tên sinh viên" /></Col>
        </Row>
        <Row gutter={12} style={{marginTop:8}}>
          <Col span={12}><label>Từ ngày</label><Input type="date" value={lookupForm.startDate} onChange={e=>setLookupForm({...lookupForm, startDate: e.target.value})} /></Col>
          <Col span={12}><label>Đến ngày</label><Input type="date" value={lookupForm.endDate} onChange={e=>setLookupForm({...lookupForm, endDate: e.target.value})} /></Col>
        </Row>
        <Row gutter={12} style={{marginTop:8}}>
          <Col span={12}><label>invoiceType</label><Input value={lookupForm.invoiceType} onChange={e=>setLookupForm({...lookupForm, invoiceType: e.target.value})} placeholder="VD: 02GTTT" /></Col>
          <Col span={12}><label>Ký hiệu</label><Input value={lookupForm.invoiceSeri} onChange={e=>setLookupForm({...lookupForm, invoiceSeri: e.target.value})} placeholder="VD: C22TYT" /></Col>
        </Row>
        <Row gutter={12} style={{marginTop:12}}>
          <Col span={24}>
            <Button type="primary" icon={<SearchOutlined />} onClick={async()=>{
              try {
                const resp = await fetch(`${apiBase}/tuition/portal/search-issued`, { method: 'POST', headers: { 'Content-Type':'application/json', ...authHeader() }, body: JSON.stringify(lookupForm) });
                const data = await resp.json();
                if (!resp.ok || !data?.success) { throw new Error((data?.detail || data?.message) || 'Tra cứu thất bại'); }
                setLookupForm(f=>({ ...f, result: data.data }));
              } catch(e:any) { message.error(e?.message || 'Tra cứu thất bại'); }
            }}>Tra cứu</Button>
          </Col>
        </Row>
        {Array.isArray(lookupForm.result) && lookupForm.result.length>0 && (
          <div style={{marginTop:12}}>
            <Table rowKey={(r:any)=>r.invoiceNo+String(r.createdDate)} size="small" pagination={{pageSize:6}} scroll={{ x: 1000, y: 360 }} dataSource={lookupForm.result} columns={[
              {title:'Số hóa đơn', dataIndex:'invoiceNo', width: 120, ellipsis:true},
              {title:'Ký hiệu', dataIndex:'invoiceSeri', width: 90, ellipsis:true},
              {title:'Ngày', dataIndex:'createdDate', width: 180, ellipsis:true},
              {title:'Người mua', dataIndex:'buyerName', width: 180, ellipsis:true},
              {title:'Địa chỉ', dataIndex:'buyerAddress', width: 220, ellipsis:true},
              {title:'Tên hàng hóa', dataIndex:'itemName', width: 220, ellipsis:true},
              {title:'Tổng tiền', dataIndex:'totalAmountWithVAT', width: 130, align:'right', render:(v:number)=> new Intl.NumberFormat('vi-VN').format(Number(v||0))},
              {title:'Hành động', key:'act', fixed:'right', width: 80, render: (_:any, r:any)=> <Button type="link" onClick={async()=>{
                try{
                  const resp = await fetch(`${apiBase}/tuition/invoice/link`, { method:'POST', headers:{ 'Content-Type':'application/json', ...authHeader() }, body: JSON.stringify({ MaHS: tuitionForm.MaHS, buyerName: r.buyerName, buyerAddress: r.buyerAddress, amount: r.totalAmountWithVAT, invoiceNo: r.invoiceNo }) });
                  const d = await resp.json();
                  if(!resp.ok || !d?.success){ throw new Error(d?.message || 'Liên kết thất bại'); }
                  message.success('Đã liên kết số hóa đơn');
                }catch(e:any){ message.error(e?.message || 'Liên kết thất bại'); }
              }}>Chọn</Button>}
            ]} />
          </div>
        )}
      </Modal>

      <Modal open={statModalOpen} onCancel={()=>setStatModalOpen(false)} footer={[<Button key="close" onClick={()=>setStatModalOpen(false)}>Đóng</Button>]} title="Thống kê học phí theo Khóa" width={1000} styles={{ body: { maxHeight: 560, overflowY: 'auto' } }}>
        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col span={6}><Statistic title="Tổng bản ghi" value={statData.total} /></Col>
          <Col span={6}><Statistic title="Đã nộp" value={statData.paid} /></Col>
          <Col span={6}><Statistic title="Chưa nộp" value={statData.unpaid} /></Col>
          <Col span={6}><Statistic title="Tổng tiền" value={statData.totalAmount} suffix="đ" /></Col>
        </Row>
        {/* Progress đã chuyển sang modal đồng bộ */}
        <Row gutter={16} style={{ marginBottom: 12 }}>
          <Col span={12}><Statistic title="Thanh toán TM" value={statData.tmCount || 0} /></Col>
          <Col span={12}><Statistic title="Thanh toán CK" value={statData.ckCount || 0} /></Col>
        </Row>
        <Table
          rowKey={(r) => `${r.TuitionID || r.id || Math.random()}`}
          dataSource={statData.details}
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ y: 360, x: true }}
          columns={[
            { title: 'Mã SV', dataIndex: 'MaHS', width: 120, ellipsis: true },
            { title: 'Họ tên', dataIndex: 'HoTen', width: 180, ellipsis: true },
            { title: 'Lớp', dataIndex: 'TenLop', width: 120, ellipsis: true },
            { title: 'Kỳ', dataIndex: 'Semester', width: 80, ellipsis: true },
            { title: 'Năm', dataIndex: 'Year', width: 110, ellipsis: true },
            { title: 'Tên khoản', dataIndex: 'ItemName', width: 220, ellipsis: true },
            { title: 'Số lượng', dataIndex: 'Quantity', width: 90, align: 'right' },
            { title: 'Đơn giá', dataIndex: 'UnitPrice', width: 130, align: 'right', render: (v:number)=> new Intl.NumberFormat('vi-VN').format(Number(v||0)) },
            { title: 'Thành tiền', dataIndex: 'TotalAmount', width: 140, align: 'right', render: (v:number)=> new Intl.NumberFormat('vi-VN').format(Number(v||0)) },
            { title: 'TT nộp', dataIndex: 'PaymentStatus', width: 100, render: (v:boolean)=> v ? 'Đã nộp' : 'Chưa nộp' },
            { title: 'Tiền mặt', dataIndex: 'PaymentMethod', width: 70, render:(v:string)=> { const pm = String(v||'').toUpperCase(); return (pm === 'TM' || pm.includes('TIỀN MẶT') || pm.includes('TIEN MAT')) ? '✓' : ''; } },
            { title: 'Chuyển khoản', dataIndex: 'PaymentMethod', width: 70, render:(v:string)=> { const pm = String(v||'').toUpperCase(); return (pm === 'CK' || pm.includes('CHUYỂN KHOẢN') || pm.includes('CHUYEN KHOAN')) ? '✓' : ''; } },
            { title: 'Số HĐ', dataIndex: 'InvoiceNumber', width: 140, ellipsis:true },
          ]}
        />
        <div style={{ textAlign: 'right', marginTop: 12 }}>
          <Button onClick={() => {
            const rows = statData.details.map(r => ({
              MaHS: r.MaHS, HoTen: r.HoTen, Lop: r.TenLop, Ky: r.Semester, Nam: r.Year, TenKhoan: r.ItemName, SoLuong: r.Quantity, DonGia: r.UnitPrice, ThanhTien: r.TotalAmount, TrangThai: r.PaymentStatus ? 'Đã nộp' : 'Chưa nộp'
            }));
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rows);
            XLSX.utils.book_append_sheet(wb, ws, 'ThongKe');
            XLSX.writeFile(wb, 'thong_ke_hoc_phi_theo_khoa.xlsx');
          }} icon={<DownloadOutlined />}>Xuất Excel</Button>
        </div>
      </Modal>

      <Modal open={configModalOpen} onCancel={()=>{setConfigModalOpen(false); resetConfigForm();}} onOk={saveConfig} title="Cấu hình học phí theo Khóa/Kỳ/Năm" okText={isEditingConfig ? "Cập nhật" : "Thêm mới"} cancelText="Đóng" width={900}>
        <Row gutter={12}>
          <Col span={8}><label>Khóa học</label><Select value={configForm.KhoaHocID || selectedCourse} onChange={v=>setConfigForm({...configForm, KhoaHocID: v})} options={courses.map(c=>({value:c.KhoaHocID,label:c.TenKhoaHoc}))} style={{width:'100%'}} disabled={isEditingConfig} /></Col>
          <Col span={8}><label>Kỳ</label><Input value={configForm.Semester} onChange={e=>setConfigForm({...configForm, Semester: e.target.value})} placeholder="VD: HK1" disabled={isEditingConfig} /></Col>
          <Col span={8}><label>Năm</label><Input value={configForm.Year} onChange={e=>setConfigForm({...configForm, Year: e.target.value})} placeholder="VD: 2025-2026" disabled={isEditingConfig} /></Col>
        </Row>
        <Row gutter={12} style={{marginTop:8}}>
          <Col span={12}><label>Tên khoản thu</label><Input value={configForm.ItemName} onChange={e=>setConfigForm({...configForm, ItemName: e.target.value})} /></Col>
          <Col span={6}><label>Đơn vị</label><Input value={configForm.UnitName} onChange={e=>setConfigForm({...configForm, UnitName: e.target.value})} /></Col>
          <Col span={6}><label>Đơn giá</label><Input type="number" value={configForm.UnitPrice} onChange={e=>setConfigForm({...configForm, UnitPrice: Number(e.target.value)})} /></Col>
        </Row>
        {isEditingConfig && (
          <Row style={{marginTop:8}}>
            <Col><Button onClick={resetConfigForm}>Hủy sửa</Button></Col>
          </Row>
        )}
        <div style={{marginTop:12}}>
          <Table rowKey="ConfigID" dataSource={configs} pagination={{pageSize:5}} size="small" columns={[
            {title:'Khóa học', dataIndex:'KhoaHocID', width: 100},
            {title:'Kỳ', dataIndex:'Semester', width: 80},
            {title:'Năm', dataIndex:'Year', width: 100},
            {title:'Tên khoản', dataIndex:'ItemName', width: 180},
            {title:'Đơn vị', dataIndex:'UnitName', width: 80},
            {title:'Đơn giá', dataIndex:'UnitPrice', width: 120, render: (v: number) => new Intl.NumberFormat('vi-VN').format(Number(v || 0))},
            {title:'Thao tác', key:'action', width: 120, render: (_: any, record: any) => (
              <Space>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => editConfig(record)}>Sửa</Button>
                <Popconfirm title="Xác nhận xóa cấu hình này?" okText="Xóa" cancelText="Hủy" onConfirm={() => handleDeleteConfig(record.ConfigID)}>
                  <Button type="link" size="small" danger icon={<DeleteOutlined />}>Xóa</Button>
                </Popconfirm>
              </Space>
            )}
          ]} />
        </div>
      </Modal>

      <Modal
        open={classStatModalOpen}
        onCancel={()=>setClassStatModalOpen(false)}
        title={`Thống kê học phí theo lớp: ${classes.find(c=>c.LopHocID===selectedClass)?.TenLop || ''}`}
        width={1200}
        footer={[
          <Button key="export" icon={<DownloadOutlined />} onClick={() => {
            const rows = [
              ...classStatData.paidStudents.map(s => ({
                MaHS: s.MaHS,
                HoTen: s.HoTen || s.Hoten,
                Lop: s.TenLop,
                TrangThai: 'Đã nộp',
                SoTien: s.TotalAmount || 0,
                PhuongThuc: s.PaymentMethod || '',
                SoHoaDon: s.InvoiceNumber || ''
              })),
              ...classStatData.unpaidStudents.map(s => ({
                MaHS: s.MaHS,
                HoTen: s.HoTen || s.Hoten,
                Lop: s.TenLop,
                TrangThai: 'Chưa nộp',
                SoTien: 0,
                PhuongThuc: '',
                SoHoaDon: ''
              }))
            ];
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rows);
            XLSX.utils.book_append_sheet(wb, ws, 'ThongKeLop');
            XLSX.writeFile(wb, `thong_ke_lop_${classes.find(c=>c.LopHocID===selectedClass)?.TenLop || 'unknown'}.xlsx`);
          }}>Xuất Excel</Button>,
          <Button key="close" onClick={()=>setClassStatModalOpen(false)}>Đóng</Button>
        ]}
        styles={{ body: { maxHeight: 600, overflowY: 'auto' } }}
      >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}><Statistic title="Tổng sinh viên" value={classStatData.total} /></Col>
          <Col span={6}><Statistic title="Đã nộp" value={classStatData.paid} valueStyle={{ color: '#3f8600' }} /></Col>
          <Col span={6}><Statistic title="Chưa nộp" value={classStatData.unpaid} valueStyle={{ color: '#cf1322' }} /></Col>
          <Col span={6}><Statistic title="Tổng tiền" value={classStatData.totalAmount} suffix="đ" precision={0} /></Col>
        </Row>
        <Progress
          percent={classStatData.total > 0 ? Math.round((classStatData.paid / classStatData.total) * 100) : 0}
          status="active"
          style={{ marginBottom: 16 }}
        />
        <Row gutter={16}>
          <Col span={12}>
            <Card title={`Đã nộp (${classStatData.paid})`} size="small" style={{ marginBottom: 16 }}>
              <Table
                rowKey="MaHS"
                dataSource={classStatData.paidStudents}
                pagination={{ pageSize: 5 }}
                size="small"
                scroll={{ y: 200 }}
                columns={[
                  { title: 'Mã SV', dataIndex: 'MaHS', width: 120 },
                  { title: 'Họ tên', key: 'HoTen', render: (_: any, r: any) => r.HoTen || r.Hoten },
                  { title: 'Số tiền', dataIndex: 'TotalAmount', width: 120, render: (v: number) => new Intl.NumberFormat('vi-VN').format(v || 0) + 'đ' },
                  { title: 'PT', dataIndex: 'PaymentMethod', width: 60 },
                  { title: 'Số HĐ', dataIndex: 'InvoiceNumber', width: 120, ellipsis: true }
                ]}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card title={`Chưa nộp (${classStatData.unpaid})`} size="small" style={{ marginBottom: 16 }}>
              <Table
                rowKey="MaHS"
                dataSource={classStatData.unpaidStudents}
                pagination={{ pageSize: 5 }}
                size="small"
                scroll={{ y: 200 }}
                columns={[
                  { title: 'Mã SV', dataIndex: 'MaHS', width: 120 },
                  { title: 'Họ tên', key: 'HoTen', render: (_: any, r: any) => r.HoTen || r.Hoten },
                  { title: 'Lớp', dataIndex: 'TenLop' }
                ]}
              />
            </Card>
          </Col>
        </Row>
      </Modal>

      <Modal
        open={syncModalOpen}
        onCancel={()=>{ if(!syncing){ setSyncModalOpen(false); } }}
        title="Đồng bộ hóa đơn đã phát hành theo lớp"
        okText={syncing ? 'Đang đồng bộ...' : 'Đồng bộ'}
        cancelText="Đóng"
        confirmLoading={syncing}
        onOk={async()=>{
          if(!selectedClass){ message.warning('Chọn lớp trước'); return; }
          if(!syncForm.startDate || !syncForm.endDate){ message.warning('Chọn Từ ngày và Đến ngày'); return; }
          try{
            setSyncResult(null);
            setSyncing(true); setSyncProgress(5);
            const t = setInterval(()=>{
              setSyncProgress(p=> Math.min(90, p + 3));
            }, 400);
            setSyncTimer(t);
            const body = { lopHocId: selectedClass, supplierTaxCode: syncForm.supplierTaxCode || '1000319841', startDate: syncForm.startDate, endDate: syncForm.endDate, invoiceSeri: syncForm.invoiceSeri || 'C22TYT', invoiceType: syncForm.invoiceType || '02GTTT' };
            const resp = await fetch(`${apiBase}/tuition/portal/sync-class`, { method:'POST', headers:{ 'Content-Type':'application/json', ...authHeader() }, body: JSON.stringify(body) });
            const d = await resp.json();
            if(!resp.ok || !d?.success){ throw new Error(d?.message || 'Đồng bộ thất bại'); }
            setSyncResult({ total: d.total, succeeded: d.succeeded, failed: d.failed });
            setSyncProgress(100);
            message.success(`Đồng bộ xong: ${d.succeeded}/${d.total}`);
          }catch(e:any){ message.error(e?.message || 'Đồng bộ thất bại'); }
          finally{ if(syncTimer){ clearInterval(syncTimer); setSyncTimer(null);} setSyncing(false); }
        }}
      >
        <Row gutter={12}>
          <Col span={12}><label>Từ ngày</label><DatePicker style={{ width: '100%' }} onChange={(_,s)=> setSyncForm({ ...syncForm, startDate: Array.isArray(s)? s[0] : s })} /></Col>
          <Col span={12}><label>Đến ngày</label><DatePicker style={{ width: '100%' }} onChange={(_,s)=> setSyncForm({ ...syncForm, endDate: Array.isArray(s)? s[0] : s })} /></Col>
        </Row>
        <Row gutter={12} style={{ marginTop: 8 }}>
          <Col span={8}><label>MST bán</label><Input value={syncForm.supplierTaxCode} onChange={e=> setSyncForm({ ...syncForm, supplierTaxCode: e.target.value })} /></Col>
          <Col span={8}><label>Ký hiệu</label><Input value={syncForm.invoiceSeri} onChange={e=> setSyncForm({ ...syncForm, invoiceSeri: e.target.value })} /></Col>
          <Col span={8}><label>invoiceType</label><Input value={syncForm.invoiceType} onChange={e=> setSyncForm({ ...syncForm, invoiceType: e.target.value })} /></Col>
        </Row>
        {syncing && (
          <div style={{ marginTop: 12 }}>
            <Progress percent={syncProgress} />
          </div>
        )}
        {!syncing && syncResult && (
          <div style={{ marginTop: 12 }}>
            <div>Kết quả: {syncResult.succeeded}/{syncResult.total} thành công, {syncResult.failed} thất bại.</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TuitionManagementPage;


